import React, { createContext, forwardRef, useContext } from "react";
import { BaseElementProps, StyledContainerProps } from "../../props";
import {
  Typography,
  TypographyContent,
  Button,
  ButtonProps,
  ButtonVariant,
} from "../../atoms";
import { cn } from "../../styles";

interface MenuContextType {
  dense: boolean;
}

const MenuContext = createContext<MenuContextType>({ dense: false });
const useMenuContext = () => useContext(MenuContext);

export interface MenuSeparatorProps {
  /**
   * Additional CSS class name(s) for the separator.
   */
  className?: string;
}

/**
 * Horizontal divider for separating menu sections.
 * Renders as a thin line with subtle styling.
 */
const MenuSeparator: React.FC<MenuSeparatorProps> = ({ className }) => (
  <div className={cn("h-px my-1 bg-back-200 dark:bg-back-700", className)} />
);

export interface MenuGroupProps extends StyledContainerProps {
  /**
   * Title content to display for the menu group.
   * Supports localized content or plain strings.
   * @default { local: "Title" }
   */
  title: TypographyContent;
}

/**
 * Menu group component with a title label for categorizing menu items.
 * Automatically adjusts styling based on the menu's dense mode.
 */
const MenuGroup: React.FC<MenuGroupProps> = ({
  className,
  title = { local: "Title" },
  children,
}) => {
  const { dense } = useMenuContext();

  return (
    <>
      <Typography.Span
        forceWrap
        className={cn(
          dense
            ? "px-3 py-1.5 text-xs font-medium text-front-500 dark:text-front-400"
            : "px-4 py-2 text-sm font-medium text-front-500 dark:text-front-400",
          className
        )}
        content={title}
      />
      {children}
    </>
  );
};

export interface MenuButtonProps extends ButtonProps {}

/**
 * Button component designed for use within menus.
 * Inherits all Button props and automatically applies full-width variant
 * and respects the menu's dense mode.
 */
const MenuButton = forwardRef<HTMLButtonElement, MenuButtonProps>(
  (props, ref) => {
    const { dense } = useMenuContext();
    return (
      <Button ref={ref} variant={ButtonVariant.full} dense={dense} {...props} />
    );
  }
);

export interface MenuLinkProps extends BaseElementProps {
  /**
   * URL that the link navigates to.
   */
  href: string;
  /**
   * Content to display inside the link.
   */
  children: React.ReactNode;
  /**
   * Specifies where to open the linked document.
   * Common values: "_blank" (new tab), "_self" (same tab).
   */
  target?: string;
  /**
   * Relationship between the current document and the linked document.
   * Use "noopener noreferrer" when target="_blank" for security.
   */
  rel?: string;
}

/**
 * Link component styled for use within menus.
 * Provides consistent hover states and automatically adjusts to dense mode.
 * Renders as an anchor element with underlined text and full-width layout.
 */
const MenuLink: React.FC<MenuLinkProps> = ({
  className,
  href,
  children,
  target,
  rel,
  ...props
}) => {
  const { dense } = useMenuContext();

  return (
    <a
      href={href}
      target={target}
      rel={rel}
      className={cn(
        "relative overflow-hidden inline-flex items-center text-front-600 hover:bg-back-200 dark:text-front-300 dark:hover:bg-back-600 w-full cursor-pointer opacity-75 justify-center font-normal underline",
        dense ? "px-2 py-1 gap-1 text-xs" : "px-3 py-1.5 gap-2 text-sm",
        className
      )}
      {...props}
    >
      {children}
    </a>
  );
};

export interface MenuProps {
  /**
   * Additional CSS class name(s) for the menu container.
   */
  className?: string;
  /**
   * Inline styles for the menu container.
   */
  style?: React.CSSProperties;
  /**
   * Menu content including Menu.Button, Menu.Link, Menu.Group, and Menu.Separator components.
   */
  children: React.ReactNode;
  /**
   * Reduces spacing and font sizes for a more compact menu appearance.
   * Automatically applies to all child components.
   * @default false
   */
  dense?: boolean;
}

/**
 * Flexible menu component for creating dropdown menus, context menus, and action lists.
 * Provides a collection of sub-components (Button, Link, Group, Separator) that work together
 * to create well-structured menus with consistent styling. Supports dense mode for compact layouts
 * and automatically manages context to apply styling to all child components.
 */
const MenuBase = forwardRef<HTMLDivElement, MenuProps>(
  ({ className, style, children, dense = false }, ref) => {
    return (
      <MenuContext.Provider value={{ dense }}>
        <div className={cn("flex flex-col", className)} {...{ style, ref }}>
          {children}
        </div>
      </MenuContext.Provider>
    );
  }
);

const Menu = Object.assign(MenuBase, {
  Separator: MenuSeparator,
  Group: MenuGroup,
  Button: MenuButton,
  Link: MenuLink,
});

Menu.displayName = "Menu";
Menu.Separator.displayName = "Menu.Separator";
Menu.Group.displayName = "Menu.Group";
Menu.Button.displayName = "Menu.Button";
Menu.Link.displayName = "Menu.Link";

export { Menu };
